import React, { useState, useEffect, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
  Card,
  CardBody,
  Col,
  Form,
  Input,
  Label,
  Row,
  Spinner,
  Button,
} from "reactstrap";
import { Btn } from "../../../AbstractElements";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios, { AxiosError } from "axios";
import { baseUrl, mediaUrl, uploadFile } from "../../../Utils/FileUpload";
import {
  Submit,
  AddAgentTitle,
  EditAgentTitle,
  AgentTitle,
} from "../../../Utils/Constants";
import { useNavigate } from "react-router-dom";
import { MultiSelect } from "react-multi-select-component";
import { ToastContainer } from "react-toastify";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { parsePhoneNumberFromString, CountryCode } from "libphonenumber-js";
import useCustomToasts from "../../../hooks/useCustomToast";

interface FormValues {
  agent_name: string;
  phone: string;
  email: string;
  address: string;
  image?: any;
  language_id: string[];
  password?: string;
  documents: {
    name: string;
    image: any;
  }[];
  bankingdetail: string;
}

interface ErrorResponse {
  message: string;
}

interface Language {
  id: string;
  language: string;
  icon: string;
  is_deleted?: number;
  is_active?: number;
}

interface AgentFormProps {
  mode: "add" | "edit";
  agentId?: string;
}

export default function AgentForm({ mode, agentId }: AgentFormProps) {
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [languageOptions, setLanguageOptions] = useState<Language[]>([]);
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [existingFile, setExistingFile] = useState<string | null>(null);
  const [phoneInputValue, setPhoneInputValue] = useState<string>("");
  const [documentPreviews, setDocumentPreviews] = useState<{
    [key: string]: { src: string; type: string };
  }>({});
  const agentRef = useRef<any>(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { SuccessToast, ErrorToast } = useCustomToasts(navigate);

  const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      agent_name: "",
      email: "",
      phone: "",
      address: "",
      password: "",
      bankingdetail: "",
      language_id: [],
      documents: [{ name: "", image: null }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "documents",
  });

  const validatePhoneNumber = (value: string) => {
    if (!value) {
      return "Phone number is required";
    }
    const sanitizedValue = value.replace(/\D/g, "");
    const phoneNumber = parsePhoneNumberFromString(`+${sanitizedValue}`);

    if (!phoneNumber || !phoneNumber.isValid()) {
      return "Invalid phone number.";
    }

    const allowedCountries: CountryCode[] = ["IN", "SA", "AE"];
    if (!allowedCountries.includes(phoneNumber.country as CountryCode)) {
      return "Only Indian, Saudi Arabian, or UAE numbers are allowed.";
    }

    return true;
  };

  // Fetch Languages
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get(`${baseUrl}language/getall`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const activeLangs = (response.data || []).filter(
          (lang: any) => lang.is_deleted === 0 && lang.is_active === 1
        );
        setLanguageOptions(activeLangs);
      } catch (error) {
        console.error("Error fetching languages:", error);
      }
    };

    fetchLanguages();
  }, [token, mode]);

  // Fetch Agent Details if Edit Mode
  useEffect(() => {
    if (mode === "edit" && agentId) {
      const fetchAgentDetails = async () => {
        setLoading(true);
        try {
          const result = await axios.post(
            `${baseUrl}agent/viewbyid`,
            { id: agentId },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          const agent = result.data;
          agentRef.current = agent;

          const documentsData =
            agent.documents?.map((doc: any) => ({
              name: doc.name || "",
              image: doc.image || null,
            })) || [{ name: "", image: null }];

          reset({
            agent_name: agent.agent_name || "",
            email: agent.email || "",
            phone: agent.phone || "",
            address: agent.address || "",
            bankingdetail: agent.bankingdetail || "",
            language_id: agent.languages?.map((l: any) => l.id) || [],
            documents: documentsData,
          });

          setPhoneInputValue(agent.phone || "");
          setExistingFile(agent.image || null);
        } catch (error) {
          console.error("Error fetching agent details:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchAgentDetails();
    }
  }, [mode, agentId, token, reset]);

  // Document Previews for Edit mode
  useEffect(() => {
    if (!fields.length || mode !== "edit" || !agentRef.current) return;

    const previews: Record<string, { src: string; type: string }> = {};
    fields.forEach((_field, index) => {
      const doc = agentRef.current.documents?.[index];
      if (doc?.image) {
        let fileName = typeof doc.image === "string" ? doc.image : doc.image.name || "";
        const fileExt = fileName.split(".").pop()?.toLowerCase();
        const src = typeof doc.image === "string" ? `${mediaUrl}/${doc.image}` : URL.createObjectURL(doc.image);

        if (["jpg", "jpeg", "png", "gif"].includes(fileExt || "")) {
          previews[index] = { src, type: "image" };
        } else if (fileExt === "pdf") {
          previews[index] = { src, type: "pdf" };
        } else {
          previews[index] = { src, type: "other" };
        }
      }
    });

    setDocumentPreviews(previews);
  }, [fields, mode]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
    }
  };

  const onSubmit = async (data: FormValues) => {
    setIsButtonDisabled(true);
    try {
      // Profile photo upload
      let mainImageName = existingFile;
      const mainFile = data.image?.[0];
      if (mainFile) {
        mainImageName = await uploadFile(mainFile);
      } else if (mode === "add" && !existingFile) {
        ErrorToast("Image is required");
        setIsButtonDisabled(false);
        return;
      }

      // Dynamic documents upload
      const documentsPayload: { name: string; image: string }[] = [];
      for (let i = 0; i < data.documents.length; i++) {
        const doc = data.documents[i];
        if (!doc.name) {
          ErrorToast("Document name required");
          setIsButtonDisabled(false);
          return;
        }

        let uploadedName = "";
        const file = doc.image?.[0];

        if (file) {
          uploadedName = await uploadFile(file);
        } else if (mode === "edit" && typeof doc.image === "string") {
          uploadedName = doc.image;
        } else {
          ErrorToast("Image file is required");
          setIsButtonDisabled(false);
          return;
        }

        documentsPayload.push({
          name: doc.name,
          image: uploadedName,
        });
      }

      const payload: any = {
        agent_name: data.agent_name,
        phone: data.phone,
        email: data.email,
        address: data.address,
        image: mainImageName,
        language_id: data.language_id,
        documents: documentsPayload,
        bankingdetail: data.bankingdetail,
      };

      if (data.password) {
        payload.password = data.password;
      }

      if (mode === "add") {
        const response = await axios.post(`${baseUrl}agent/store`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.status === 200 || response.status === 201) {
          SuccessToast("Agent added successfully", "/view_agent");
          navigate("/view_agent");
        } else {
          ErrorToast("Failed to add agent");
        }
      } else {
        payload.id = agentId;
        const response = await axios.post(`${baseUrl}agent/update`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (response.status === 200 || response.status === 201) {
          SuccessToast("Agent updated successfully", "/view_agent");
          navigate("/view_agent");
        } else {
          ErrorToast("Failed to update agent");
        }
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "An unexpected error occurred";

      console.error("Error:", errorMessage);
      ErrorToast(errorMessage);
    } finally {
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, 1000);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }

  const langOptions = languageOptions.map((lang) => ({
    label: lang.language,
    value: lang.id,
  }));

  return (
    <>
      <Breadcrumbs
        pageTitle={mode === "add" ? AddAgentTitle : EditAgentTitle}
        parent={AgentTitle}
        title={mode === "add" ? AddAgentTitle : EditAgentTitle}
      />
      <Row>
        <Col xl={12}>
          <Card className="shadow-sm">
            <CardBody className="custom-input form-validation p-4">
              <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <Row className="g-3">
                  {/* Agent Name */}
                  <Col md={6}>
                    <Label htmlFor="agent_name" className="form-label fw-semibold">
                      Agent Name
                    </Label>
                    <Controller
                      name="agent_name"
                      control={control}
                      rules={{ required: "Agent is required" }}
                      render={({ field }) => (
                        <Input
                          id="agent_name"
                          type="text"
                          placeholder="Agent Name"
                          autoComplete="off"
                          {...field}
                          value={field.value ?? ""}
                          invalid={!!errors.agent_name}
                          style={{ borderColor: errors.agent_name ? "#dc3545" : undefined }}
                          onKeyDown={handleKeyDown}
                        />
                      )}
                    />
                    {errors.agent_name?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.agent_name.message)}
                      </div>
                    )}
                  </Col>

                  {/* Phone */}
                  <Col md={6}>
                    <Label htmlFor="phone" className="form-label fw-semibold">
                      Phone
                    </Label>
                    <Controller
                      name="phone"
                      control={control}
                      rules={{ required: "Phone number is required", validate: validatePhoneNumber }}
                      render={({ field: { onChange, value } }) => (
                        <PhoneInput
                          country={"ae"}
                          onlyCountries={["ae", "sa", "in"]}
                          value={value || phoneInputValue}
                          onChange={(phone) => {
                            setPhoneInputValue(phone);
                            onChange(phone);
                          }}
                          inputStyle={{
                            width: "100%",
                            borderColor: errors.phone ? "#dc3545" : "#ced4da",
                          }}
                          enableSearch
                          placeholder="Enter phone number"
                        />
                      )}
                    />
                    {errors.phone?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.phone.message)}
                      </div>
                    )}
                  </Col>

                  {/* Email */}
                  <Col md={6}>
                    <Label htmlFor="email" className="form-label fw-semibold">
                      Email
                    </Label>
                    <Controller
                      name="email"
                      control={control}
                      rules={{
                        required: "Email is required",
                        pattern: {
                          value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                          message: "Invalid email address",
                        },
                      }}
                      render={({ field }) => (
                        <Input
                          id="email"
                          type="email"
                          placeholder="Email"
                          autoComplete="off"
                          {...field}
                          value={field.value ?? ""}
                          invalid={!!errors.email}
                          style={{ borderColor: errors.email ? "#dc3545" : undefined }}
                          onKeyDown={handleKeyDown}
                        />
                      )}
                    />
                    {errors.email?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.email.message)}
                      </div>
                    )}
                  </Col>

                  {/* Address */}
                  <Col md={6}>
                    <Label htmlFor="address" className="form-label fw-semibold">
                      Address
                    </Label>
                    <Controller
                      name="address"
                      control={control}
                      rules={{ required: "Address is required" }}
                      render={({ field }) => (
                        <Input
                          id="address"
                          type="text"
                          placeholder="Address"
                          autoComplete="off"
                          {...field}
                          value={field.value ?? ""}
                          invalid={!!errors.address}
                          style={{ borderColor: errors.address ? "#dc3545" : undefined }}
                          onKeyDown={handleKeyDown}
                        />
                      )}
                    />
                    {errors.address?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.address.message)}
                      </div>
                    )}
                  </Col>

                  {/* Languages MultiSelect */}
                  <Col md={12}>
                    <Label className="form-label fw-semibold">Language</Label>
                    <Controller
                      name="language_id"
                      control={control}
                      rules={{
                        required: "Please select at least one language",
                      }}
                      render={({ field: { onChange, value } }) => (
                        <div style={{ border: errors.language_id ? "1px solid #dc3545" : "none", borderRadius: "6px" }}>
                          <MultiSelect
                            options={langOptions}
                            value={langOptions.filter((opt) =>
                              (value || []).includes(opt.value)
                            )}
                            onChange={(selected: any[]) => {
                              onChange(selected.map((opt) => opt.value));
                            }}
                            overrideStrings={{
                              selectSomeItems: "Select Language(s)...",
                              allItemsAreSelected: "All Languages Selected",
                            }}
                            labelledBy="Select Languages"
                          />
                        </div>
                      )}
                    />
                    {errors.language_id?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.language_id.message)}
                      </div>
                    )}
                  </Col>

                  {/* Section 2: Documents Details Container Box */}
                  <Col md={12} className="mt-4">
                    <div className="border rounded bg-light p-3">
                      <h6 className="fw-bold mb-3 text-dark">Documents Details</h6>
                      {fields.map((item, index) => (
                        <div key={item.id} className="border rounded bg-white p-3 mb-3 shadow-sm">
                          <Row className="g-3 align-items-start">
                            <Col md={6}>
                              <Label className="form-label fw-semibold">Document Name</Label>
                              <Controller
                                name={`documents.${index}.name`}
                                control={control}
                                rules={{ required: "Document name required" }}
                                render={({ field }) => (
                                  <Input
                                    type="text"
                                    placeholder="e.g. Passport, Driving License"
                                    autoComplete="off"
                                    {...field}
                                    value={field.value ?? ""}
                                    invalid={!!errors.documents?.[index]?.name}
                                    style={{ borderColor: errors.documents?.[index]?.name ? "#dc3545" : undefined }}
                                  />
                                )}
                              />
                              {errors.documents?.[index]?.name?.message && (
                                <div className="text-danger small mt-1">
                                  {String(errors.documents[index]?.name?.message)}
                                </div>
                              )}
                            </Col>
                            <Col md={6}>
                              <Label className="form-label fw-semibold">Upload Document</Label>
                              <Controller
                                name={`documents.${index}.image`}
                                control={control}
                                rules={{
                                  required:
                                    mode === "add" && !agentRef.current?.documents?.[index]?.image
                                      ? "Image file is required"
                                      : false,
                                }}
                                render={({ field: { onChange } }) => (
                                  <Input
                                    type="file"
                                    accept="image/*"
                                    invalid={!!errors.documents?.[index]?.image}
                                    style={{ borderColor: errors.documents?.[index]?.image ? "#dc3545" : undefined }}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) {
                                        if (!file.type.startsWith("image/")) {
                                          ErrorToast("Only image files (JPG, PNG, WEBP, GIF) are allowed.");
                                          e.target.value = "";
                                          return;
                                        }
                                        onChange(e.target.files);
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          setDocumentPreviews((prev) => ({
                                            ...prev,
                                            [index]: { src: reader.result as string, type: "image" },
                                          }));
                                        };
                                        reader.readAsDataURL(file);
                                      }
                                    }}
                                  />
                                )}
                              />
                              {errors.documents?.[index]?.image?.message && (
                                <div className="text-danger small mt-1">
                                  {String(errors.documents[index]?.image?.message)}
                                </div>
                              )}
                              {documentPreviews[index] && documentPreviews[index].src && (
                                <div className="mt-2">
                                  <img
                                    src={documentPreviews[index].src}
                                    alt="Preview"
                                    width="70"
                                    height="70"
                                    style={{ borderRadius: "8px", objectFit: "cover" }}
                                  />
                                </div>
                              )}
                            </Col>
                            {fields.length > 1 && (
                              <Col md={12} className="d-flex justify-content-end mt-2">
                                <Button
                                  type="button"
                                  color="danger"
                                  size="sm"
                                  onClick={() => remove(index)}
                                  style={{
                                    backgroundColor: "#e05244",
                                    borderColor: "#e05244",
                                    fontSize: "12px",
                                    padding: "4px 12px",
                                  }}
                                >
                                  Remove Document
                                </Button>
                              </Col>
                            )}
                          </Row>
                        </div>
                      ))}
                      <div className="mt-2">
                        <Button
                          type="button"
                          style={{
                            backgroundColor: "#eb8153",
                            borderColor: "#eb8153",
                            color: "#ffffff",
                            fontWeight: 500,
                            fontSize: "14px",
                            padding: "6px 16px",
                            borderRadius: "6px",
                          }}
                          onClick={() => append({ name: "", image: null })}
                        >
                          + Add Document
                        </Button>
                      </div>
                    </div>
                  </Col>

                  {/* Section 3: Banking Details Container Box */}
                  <Col md={12} className="mt-4">
                    <div className="border rounded bg-light p-3">
                      <h6 className="fw-bold mb-3 text-dark">Banking Details</h6>
                      <Row className="g-3">
                        <Col md={12}>

                          <Controller
                            name="bankingdetail"
                            control={control}
                            rules={{ required: "Banking details are required" }}
                            render={({ field }) => (
                              <Input
                                id="bankingdetail"
                                type="textarea"
                                rows={3}
                                placeholder="Enter all banking details here"
                                autoComplete="off"
                                {...field}
                                value={field.value ?? ""}
                                invalid={!!errors.bankingdetail}
                                style={{ borderColor: errors.bankingdetail ? "#dc3545" : undefined }}
                                onKeyDown={handleKeyDown}
                              />
                            )}
                          />
                          {errors.bankingdetail?.message && (
                            <div className="text-danger small mt-1">
                              {String(errors.bankingdetail.message)}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Bottom Row: Photo & Password */}
                  <Col md={6} className="mt-4">
                    <Label htmlFor="image" className="form-label fw-semibold">
                      Photo
                    </Label>
                    <Controller
                      name="image"
                      control={control}
                      rules={{
                        required: mode === "add" && !existingFile ? "Image is required" : false,
                      }}
                      render={({ field: { onChange, onBlur, ref } }) => (
                        <Input
                          id="image"
                          type="file"
                          accept="image/*"
                          invalid={!!errors.image}
                          style={{ borderColor: errors.image ? "#dc3545" : undefined }}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              if (!file.type.startsWith("image/")) {
                                ErrorToast("Only image files (JPG, PNG, WEBP, GIF) are allowed for photo.");
                                e.target.value = "";
                                return;
                              }
                              onChange(e.target.files);
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                setExistingFile(reader.result as string);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          onBlur={onBlur}
                          innerRef={ref}
                          onKeyDown={handleKeyDown}
                        />
                      )}
                    />
                    {errors.image?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.image.message)}
                      </div>
                    )}
                    {existingFile && (
                      <img
                        src={
                          existingFile.startsWith("data:")
                            ? existingFile
                            : `${mediaUrl}/${existingFile}`
                        }
                        alt="Preview"
                        width="70"
                        height="70"
                        style={{ marginTop: "5px", borderRadius: "10px" }}
                      />
                    )}
                  </Col>

                  <Col md={6} className="mt-4">
                    <Label htmlFor="password" className="form-label fw-semibold">
                      Password
                    </Label>
                    <div className="position-relative">
                      <Controller
                        name="password"
                        control={control}
                        rules={{
                          required: mode === "add" ? "Password is required" : false,
                          minLength: {
                            value: 6,
                            message: "Password must be at least 6 characters",
                          },
                        }}
                        render={({ field }) => (
                          <Input
                            id="password"
                            type={isPasswordVisible ? "text" : "password"}
                            placeholder={mode === "add" ? "Enter password" : "Leave blank to keep unchanged"}
                            autoComplete="new-password"
                            {...field}
                            value={field.value ?? ""}
                            invalid={!!errors.password}
                            style={{ borderColor: errors.password ? "#dc3545" : undefined }}
                            onKeyDown={handleKeyDown}
                          />
                        )}
                      />
                      <span
                        onClick={togglePasswordVisibility}
                        style={{
                          position: "absolute",
                          right: "12px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          cursor: "pointer",
                          color: "#6c757d",
                          fontSize: "14px",
                        }}
                      >
                        {isPasswordVisible ? "Hide" : "Show"}
                      </span>
                    </div>
                    {errors.password?.message && (
                      <div className="text-danger small mt-1">
                        {String(errors.password.message)}
                      </div>
                    )}
                  </Col>

                  {/* Submit Button */}
                  <Col md={12} className="mt-4">
                    <Btn color="primary" disabled={isButtonDisabled}>
                      {mode === "add" ? Submit : "Update"}
                    </Btn>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <ToastContainer toastStyle={{ userSelect: "none" }} />
    </>
  );
}
